import { Component, inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  Reward,
  RewardControllerService,
  ShippingOrderItemDetail
} from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-shipping-order-products-refund-modal',
  templateUrl: './shipping-order-products-refund-modal.component.html',
  styleUrl: './shipping-order-products-refund-modal.component.scss'
})
export class ShippingOrderProductsRefundModalComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private rewardService = inject(RewardControllerService);

  items: ShippingOrderItemDetail[] = this.config.data.items || [];
  rewards: Reward[] = this.config.data.rewards || [];
  availableItems: ShippingOrderItemDetail[][];

  form = new FormGroup({
    items: new FormArray([this.newFormGroup])
  });

  ngOnInit(): void {
    if (!this.rewards.length) {
      this.ref.close();
      return;
    }
    if (this.items?.length === 1) {
      this.form.controls.items.at(0).setValue({
        glampoints: Math.abs(this.reward(this.items[0].rewardId).points),
        productVariantId: this.items[0].productVariantId,
        rewardId: this.items[0].rewardId
      });
      this.availableItems = [this.items];
    } else this.setAvailableItems();
  }

  setAvailableItems() {
    this.availableItems = this.form.value.items.map((i, ind) =>
      this.items.filter(
        (pv) =>
          this.reward(pv.rewardId) &&
          !this.refund(pv.rewardId) &&
          !this.form.value.items?.some(
            (fi, ind2) =>
              fi.productVariantId === pv.productVariantId && ind2 !== ind
          )
      )
    );
    if (
      this.form.value.items.length === 1 &&
      this.availableItems?.length === 1 &&
      this.availableItems[0]?.length === 1
    ) {
      this.form.controls.items.at(0).setValue({
        glampoints: Math.abs(
          this.reward(this.availableItems[0][0].rewardId).points
        ),
        productVariantId: this.availableItems[0][0].productVariantId,
        rewardId: this.availableItems[0][0].rewardId
      });
    }
  }

  setGlampoints(index: number) {
    if (
      this.form.value.items.at(index)?.productVariantId &&
      this.items?.some(
        (i) =>
          i.productVariantId ===
            this.form.value.items.at(index).productVariantId && i.rewardId
      )
    ) {
      this.form.controls.items
        .at(index)
        .controls.rewardId.setValue(
          this.items.find(
            (i) =>
              i.productVariantId ===
                this.form.value.items.at(index).productVariantId && i.rewardId
          ).rewardId
        );
      this.form.controls.items
        .at(index)
        .controls.glampoints.setValue(
          Math.abs(this.reward(this.form.value.items.at(index).rewardId).points)
        );
    }
    this.setAvailableItems();
  }

  get newFormGroup() {
    return new FormGroup({
      productVariantId: new FormControl<number>(null, Validators.required),
      glampoints: new FormControl<number>({ value: null, disabled: true }, [
        Validators.required,
        Validators.min(1)
      ]),
      rewardId: new FormControl<number>(null, [
        Validators.required,
        Validators.min(1)
      ])
    });
  }

  reward(rewardId: number) {
    return this.rewards?.find((r) => r.rewardId === rewardId);
  }

  refund(rewardId: number) {
    return this.rewards?.find(
      (r) => r.rewardType === 6 && r.referenceId === rewardId
    );
  }

  addProduct() {
    if (this.hasProductToRefund) {
      this.form.controls.items.push(this.newFormGroup);
      this.setAvailableItems();
    }
  }

  async refundProducts() {
    if (this.form.valid) {
      try {
        LoaderService.showLoader();
        await Promise.all(
          this.form.value.items.map((i) =>
            lastValueFrom(
              this.rewardService
                .forceCancelTradeReward(i.rewardId)
                .pipe(map((data) => data.result))
            )
          )
        );
        this.ref.close(true);
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  get refunds() {
    return this.rewards?.filter((r) => r.rewardType === 6);
  }

  get hasProductToRefund() {
    return (
      this.items?.length >
      this.form.value.items.length + (this.refunds?.length || 0)
    );
  }

  get totalGlampoints() {
    return this.form
      ?.getRawValue()
      ?.items?.reduce((sum, i) => (sum += i.glampoints || 0), 0);
  }
}
